import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, User, FileDown, FileText, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import TopHeader from "@/components/TopHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate, formatMinutesAsHours, formatHoursShort, formatCurrency } from "@/lib/format";

export default function ProjektDetail() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [totals, setTotals] = useState(null);
    const [users, setUsers] = useState([]);

    const load = useCallback(async () => {
        try {
            const [p, t, u] = await Promise.all([
                api.get(`/projects/${id}`),
                api.get(`/projects/${id}/totals`),
                api.get("/users"),
            ]);
            setProject(p.data); setTotals(t.data); setUsers(u.data || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    if (!project || !totals) return (
        <div><TopHeader title="Projekt" /><div className="p-6 text-center text-mes-muted">Lade...</div></div>
    );

    const allowanceMin = (project.stundenkontingent || 0) * 60;
    const total = totals.total_minutes || 0;
    const included = totals.included_minutes || 0;
    const extra = totals.extra_minutes || 0;
    const extraValue = totals.extra_value_eur || 0;
    const pct = allowanceMin ? Math.min(999, (total / allowanceMin) * 100) : 0;
    const level = pct >= 100 ? "kritisch" : pct >= 90 ? "hoch" : pct >= 80 ? "achtung" : "ok";
    const color = level === "kritisch" ? "bg-mes-danger"
        : level === "hoch" || level === "achtung" ? "bg-mes-warn" : "bg-mes-brand";
    const userName = (uid) => users.find((u) => u.id === uid)?.name || "?";

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/reports/pdf/${id}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `Baustellenjournal_${project.name.replace(/\s+/g, "_")}.pdf`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(link.href), 1000);
            toast.success("PDF erstellt");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF-Export fehlgeschlagen"); }
    };
    const downloadCsv = async () => {
        try {
            const res = await api.get(`/reports/csv`, { params: { project_id: id }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `zeiten_${project.name.replace(/\s+/g, "_")}.csv`; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { toast.error("CSV-Export fehlgeschlagen"); }
    };

    return (
        <div>
            <TopHeader title="Projekt" />
            <main className="mx-auto max-w-2xl px-4 pt-4 safe-bottom" data-testid="page-projekt-detail">
                <Link to="/projekt" className="mb-3 inline-flex items-center gap-1 text-sm text-mes-brand hover:underline" data-testid="link-back-projects">
                    <ArrowLeft className="h-4 w-4" /> Zurück zu Projekten
                </Link>
                <Card className="mes-card p-4">
                    <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">{project.customer}</div>
                    <h1 className="font-display text-xl font-bold text-mes-ink" data-testid="project-name">{project.name}</h1>
                    <div className="mt-2 space-y-1 text-sm text-mes-text">
                        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-mes-brand" /> {project.address}</div>
                        <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-mes-brand" /> Baubeginn: {formatDate(project.start_date + "T00:00:00")}</div>
                        <div className="flex items-center gap-2"><User className="h-4 w-4 text-mes-brand" /> {(project.assigned_user_ids || []).length} Zugewiesene</div>
                    </div>
                </Card>

                <Card className="mes-card mt-4 p-4" data-testid="allowance-detail">
                    <div className="flex items-center justify-between">
                        <div className="font-display text-sm font-semibold text-mes-ink">Stundenkontingent</div>
                        {level !== "ok" && (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                                level === "kritisch" ? "bg-mes-danger/10 text-mes-danger" : "bg-mes-warnSoft text-mes-warn"
                            }`}><AlertTriangle className="h-3.5 w-3.5" /> {pct.toFixed(0)}%</span>
                        )}
                    </div>
                    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-mes-surfaceAlt ring-1 ring-mes-border">
                        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        <MetricBox label="Kontingent" value={`${formatMinutesAsHours(allowanceMin)} h`} />
                        <MetricBox label="Enthalten" value={`${formatMinutesAsHours(included)} h`} tone="brand" />
                        <MetricBox label="Zusatz" value={`${formatMinutesAsHours(extra)} h`} tone={extra > 0 ? "warn" : "muted"} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                        <MetricBox label="Satz Zusatz" value={`${formatCurrency(project.hourly_rate_extra || 0)} / h`} />
                        <MetricBox label="Wert Zusatz" value={formatCurrency(extraValue)} tone={extra > 0 ? "warn" : "muted"} />
                    </div>
                </Card>

                <Card className="mes-card mt-4 p-4" data-testid="hours-per-user">
                    <div className="font-display text-sm font-semibold text-mes-ink mb-2">Stunden je Mitarbeiter</div>
                    <div className="divide-y divide-mes-border">
                        {Object.entries(totals.per_user || {}).sort((a, b) => b[1] - a[1]).map(([uid, min]) => (
                            <div key={uid} className="flex justify-between py-1.5 text-sm">
                                <span className="text-mes-text">{userName(uid)}</span>
                                <span className="tabular font-semibold text-mes-ink">{formatHoursShort(min)}</span>
                            </div>
                        ))}
                        {Object.keys(totals.per_user || {}).length === 0 && (
                            <div className="py-2 text-sm text-mes-muted">Noch keine Zeiten erfasst.</div>
                        )}
                    </div>
                </Card>

                <div className="mt-4 grid grid-cols-2 gap-3">
                    <Button onClick={downloadPdf} data-testid="btn-download-pdf"
                        className="mes-btn-primary h-11">
                        <FileText className="mr-2 h-4 w-4" /> PDF
                    </Button>
                    <Button onClick={downloadCsv} data-testid="btn-download-csv"
                        variant="outline" className="h-11 border-mes-border bg-white text-mes-ink hover:bg-mes-surfaceAlt">
                        <FileDown className="mr-2 h-4 w-4" /> CSV
                    </Button>
                </div>
            </main>
        </div>
    );
}

function MetricBox({ label, value, tone = "default" }) {
    const toneCls = tone === "brand" ? "text-mes-brand bg-mes-brandSoft"
        : tone === "warn" ? "text-mes-warn bg-mes-warnSoft"
            : tone === "muted" ? "text-mes-muted bg-mes-surfaceAlt"
                : "text-mes-ink bg-mes-surfaceAlt";
    return (
        <div className={`rounded-md p-2 ${toneCls}`}>
            <div className="text-[10px] font-medium uppercase tracking-widest opacity-70">{label}</div>
            <div className="mt-0.5 tabular text-sm font-semibold">{value}</div>
        </div>
    );
}
